/**
 * IS THE BALL IN OUR COURT? — one answer, read by the board card's border, its
 * pulsing dot, its highlighted stage line and its footer.
 *
 * There is no new definition of "needs Gabriel" here and there must not be: the
 * actions come from the server already computed and already ordered
 * (src/actions/model.js), and a second opinion on the client is exactly how the
 * board and Today start disagreeing about which company matters. This module
 * only decides WHICH of the actions the card already has earns the treatment.
 *
 * Three exclusions, each one a rule the system already holds elsewhere:
 *
 *  - OVERDUE actions get nothing. Same reason ActionChip drops to the neutral
 *    outline past the stale threshold: a 96-day-old "reply waiting" dressed as
 *    today's work is how a to-do list stops being read. Those live in Follow-ups,
 *    and the card still shows its chip.
 *
 *  - A FIXTURE IS NOT A DEBT. A meeting today or tomorrow is not waiting on
 *    anybody — it is going to happen — and it outranks every debt behind it on
 *    purpose (the enmacc case in model.js: when you are speaking to someone this
 *    afternoon, telling you to email them first is advice you would ignore). So a
 *    card led by a meeting keeps its date line and takes no treatment, which is
 *    also why Today's meeting rows carry no verb.
 *
 *  - A WAIT WE CANNOT DATE IS NOT CLAIMED. The treatment says "this has been on
 *    you for N days", so it needs a real age: `overdue` is false for an action
 *    with no anchor date, and inferring "live" from a missing timestamp is the
 *    mistake the agreement line already made once. Such a card keeps its chip,
 *    whose detail says what it knows.
 *
 * The action returned is the card's TOP one — the same action the chip names — so
 * the two halves of the card cannot describe different work.
 */

import { CALL_ACTIONS } from "./callActions.mjs";

// Actions that are appointments rather than debts. Both are statements about
// something that will happen without us doing anything to it.
const FIXTURES = new Set(["meeting_today", "meeting_soon"]);

/**
 * The action a card is waiting on Gabriel for, or null. `chip` is one entry of
 * GET /board-chips.
 */
export function waitingAction(chip) {
  // Action items from a call have their own tag on the card, so, like the stage
  // line's chip (StageLine.js), this reads the top action other than them.
  const top = chip && chip.actions && chip.actions.find((a) => a.kind !== CALL_ACTIONS);
  if (!top || top.overdue) return null;
  if (FIXTURES.has(top.kind)) return null;
  // Finite and not in the future: an anchor we can count from.
  if (!Number.isFinite(top.age_days) || top.age_days < 0) return null;
  return top;
}

/**
 * How long it has been sitting there, in the words Follow-ups already uses.
 * Zero is "today" rather than "waiting 0d" — a lead that arrived this morning is
 * the most urgent thing on the board and it has not waited a day yet.
 */
export function waitedLabel(action) {
  const days = action && action.age_days;
  if (!Number.isFinite(days)) return null;
  return days >= 1 ? `waiting ${days}d` : "today";
}
